import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface AuthRecoveryState {
  showRecovery: boolean;
  reason: 'session_expired' | 'auth_error' | 'data_access_denied' | null;
}

export const useAuthRecovery = () => {
  const { isAuthenticated, isLoading, session, user } = useAuth();
  const [recoveryState, setRecoveryState] = useState<AuthRecoveryState>({
    showRecovery: false,
    reason: null,
  });

  useEffect(() => {
    // Don't show recovery while still loading
    if (isLoading) return;

    // Check for authentication deadlock conditions
    const checkAuthDeadlock = () => {
      // Case 1: User appears authenticated but no session
      if (user && !session) {
        console.warn('[AuthRecovery] User exists but no session - possible auth deadlock');
        setRecoveryState({
          showRecovery: true,
          reason: 'session_expired',
        });
        return;
      }

      // Case 2: Session exists but user is null
      if (session && !user) {
        console.warn('[AuthRecovery] Session exists but no user - possible auth deadlock');
        setRecoveryState({
          showRecovery: true,
          reason: 'auth_error',
        });
        return;
      }

      // Case 3: Auth state is inconsistent
      if (isAuthenticated && (!user || !session)) {
        console.warn('[AuthRecovery] Inconsistent auth state - possible deadlock');
        setRecoveryState({
          showRecovery: true,
          reason: 'auth_error',
        });
        return;
      }

      // Clear recovery state if everything looks good
      if (recoveryState.showRecovery && isAuthenticated && user && session) {
        setRecoveryState({
          showRecovery: false,
          reason: null,
        });
      }
    };

    checkAuthDeadlock();
  }, [isAuthenticated, isLoading, session, user, recoveryState.showRecovery]);

  // Manual trigger for data access issues
  const triggerDataAccessRecovery = () => {
    setRecoveryState({
      showRecovery: true,
      reason: 'data_access_denied',
    });
  };

  const clearRecoveryState = () => {
    setRecoveryState({
      showRecovery: false,
      reason: null,
    });
  };

  return {
    ...recoveryState,
    triggerDataAccessRecovery,
    clearRecoveryState,
  };
};